import {
	Box,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Link,
} from "@mui/material";
import { Fragment, memo, useEffect, useState } from "react";
import EventIcon from "@mui/icons-material/Event";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { textNeutralTertiary } from "../../../core/theme";
import { formatStringDate } from "../../utils/dateUtils";
import LocalTime from "../LocalTime/LocalTime";
import StyledTextarea from "../StyledTextarea";
import { style } from "../ActivityModal/ActivityModal.style";
import { useLazyGetContractHistoryQuery } from "../../../requests_cm/gecoContractsService/service";
import { ContractHistoryItemType } from "../../../requests_cm/gecoContractsService/types";
import { useLazyGetCommentsQuery, useCreateCommentMutation } from "../../../requests_cm/gepoCommentService/service";
import { CommentObjectClass, CommentType } from "../../../requests_cm/gepoCommentService/types";
import { PrimaryButton } from "../CustomButton";
import { useRtkQueryDynamicEndpoint } from "../../hooks/useRtkQueryDynamicEndpoint";

const statusByCode: Record<number, string> = {
	20: "To review",
	21: "Ready for full validation",
	30: "Valid",
	100: "Legacy inactive",
};

const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const trailingPunctuationCharacters = new Set([".", ",", "!", "?", ";", ":", ")"]);

const isUrl = (text: string) => /^https?:\/\/[^\s]+$|^www\.[^\s]+$/i.test(text);

const getTrailingPunctuation = (text: string) => {
	let punctuationStart = text.length;
	while (punctuationStart > 0 && trailingPunctuationCharacters.has(text[punctuationStart - 1])) {
		punctuationStart -= 1;
	}
	return text.slice(punctuationStart);
};

const renderCommentText = (comment: string) =>
	comment.split(urlRegex).map((part, index) => {
		if (!isUrl(part)) {
			return part;
		}

		const trailingPunctuation = getTrailingPunctuation(part);
		const url = trailingPunctuation ? part.slice(0, -trailingPunctuation.length) : part;
		const href = url.startsWith("www.") ? `https://${url}` : url;
		return (
			<Fragment key={`${part}-${index}`}>
				<Link href={href} target="_blank" rel="noopener noreferrer">
					{url}
				</Link>
				{trailingPunctuation}
			</Fragment>
		);
	});

export interface ActivityLogModalProps {
	onClose: () => void;
	isOpen: boolean;
	resourceId: number | undefined;
	resourceName: string | undefined;
	resource: string;
	resourceLabel: string;
	commentObjectClass: CommentObjectClass;
	title: string;
}

function ActivityLogModal({ onClose, isOpen, resourceName, resourceId, resource, resourceLabel, commentObjectClass, title }: Readonly<ActivityLogModalProps>) {
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const [page, setPage] = useState(1);
	const [commentText, setCommentText] = useState("");
	const [getContractHistoryBase, { data: history, isLoading }] = useLazyGetContractHistoryQuery();
	const [getComments, { data: comments }] = useLazyGetCommentsQuery();
	const [createComment, { isLoading: isSendingComment }] = useCreateCommentMutation();
	const getContractHistory = useRtkQueryDynamicEndpoint(getContractHistoryBase);

	useEffect(() => {
		if (resourceId && isOpen) {
			getContractHistory({ contract_id: resourceId, resource, page, per_page: 50 });
		}
	}, [resourceId, resource, page, isOpen]);

	useEffect(() => {
		if (resourceId && isOpen) {
			getComments({ object_class: commentObjectClass, object_id: resourceId, page: 1, per_page: 50 });
		}
	}, [resourceId, commentObjectClass, isOpen]);

	useEffect(() => {
		setPage(1);
	}, [resourceId]);

	const closeModal = () => {
		setPage(1);
		setCommentText("");
		onClose();
	};

	const sendComment = () => {
		if (resourceId && commentText.trim()) {
			createComment({ object_class: commentObjectClass, object_id: resourceId, comment: commentText.trim() });
			setCommentText("");
		}
	};

	const getStatusLabel = (status: unknown) => {
		if (typeof status === "number") {
			return statusByCode[status] ?? String(status);
		}
		return String(status);
	};

	const isValidationUpdate = (entry: ContractHistoryItemType) => {
		const nextStatus = entry.changeset.status[1];
		return entry.operation === "UPDATE" && (nextStatus === 30 || nextStatus === "valid");
	};

	return (
		<Dialog PaperProps={{ sx: style.dialog }} maxWidth="md" onClose={closeModal} open={isOpen}>
			<DialogTitle sx={style.dialogTitle}>
				<Box alignItems="center" display="flex">
					<MessageOutlinedIcon className="message-icon" />
					{title}
				</Box>
				<IconButton aria-label="close" onClick={closeModal}><CloseIcon /></IconButton>
			</DialogTitle>
			<DialogContent>
				<Box sx={style.name}>{resourceLabel}: <Box component="span">{resourceName}</Box></Box>
				{history?.result
					.filter((entry) => Object.hasOwn(entry.changeset, "status"))
					.map((entry: ContractHistoryItemType, index) => (
						<Box key={`${entry.date}-${index}`} sx={style.systemComment}>
							<EventIcon />
							<Box>
								<Box component="div">
									{isValidationUpdate(entry) ? (
										<>{resourceLabel} validated by <Box component="strong">{entry.user}</Box></>
									) : (
										<>{resourceLabel} {entry.operation === "INSERT" ? "created" : "updated"} and status is now: <Box component="span">{getStatusLabel(entry.changeset.status[1])}</Box> (by {entry.user})</>
									)}
								</Box>
								<LocalTime utcDate={entry.date}>
									<Box sx={style.createdAt}>{formatStringDate(entry.date, "dd/MM/yyyy, HH:mm", timezone)}</Box>
								</LocalTime>
							</Box>
						</Box>
					))}
				{isLoading && <Box display="flex" justifyContent="center"><CircularProgress sx={{ margin: "40px" }} data-testid="loader" /></Box>}
				{!isLoading && history && !history.result.some((entry) => Object.hasOwn(entry.changeset, "status")) && <Box display="flex" margin="20px" justifyContent="center" color={textNeutralTertiary}>No Activity found</Box>}
				{!isLoading && history && page < history.pages && <Box display="flex" margin="12px" justifyContent="center" color={textNeutralTertiary}><Link sx={{ cursor: "pointer" }} onClick={() => setPage((current) => current + 1)}>Load more</Link></Box>}
				{comments?.data
					.filter((comment) => comment.type === CommentType.USER)
					.map((comment) => (
						<Box key={comment.id} sx={style.manualComment}>
							<Box component="div">{comment.creator.first_name} {comment.creator.last_name} has added a comment.</Box>
							<Box sx={style.commentContent}>{renderCommentText(comment.comment)}</Box>
						</Box>
					))}
				<Box display="flex" gap={1} marginTop={2}>
					<StyledTextarea
						sx={{ width: "100%" }}
						minRows={2}
						placeholder="Add a comment"
						value={commentText}
						disabled={isSendingComment}
						onChange={(event) => setCommentText(event.target.value)}
					/>
					<PrimaryButton
						text="Send"
						startIcon={<SendIcon />}
						sx={{ minWidth: "auto" }}
						onClick={sendComment}
						isDisabled={isSendingComment || !commentText.trim()}
					/>
				</Box>
			</DialogContent>
			<DialogActions sx={{ justifyContent: "flex-end" }}>
				<PrimaryButton text="Close" onClick={closeModal} />
			</DialogActions>
		</Dialog>
	);
}

export default memo(ActivityLogModal);